"use client";

import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";
import { resolveCssColor, rgbString } from "../lib/css-color";

/*
  Site choreography — one gesture for the whole site: the "precision" ease (a long expo-out).
  Everything is driven by classes and data attributes rendered by the server components,
  for any number of elements:
    [data-reveal]                 fade/rise on scroll (staggered in the hero intro)
    [data-heading]                masked line reveal of display headings
    [data-parallax="n"]           scrubbed vertical drift of n % (an <img> inside an overflow-hidden .ph)
    [data-hours] [data-day]       today's row gets .is-today (works without GSAP)
    [data-reveal-frame]           before/after slider: drag anywhere + hidden range input (works without GSAP)
    [data-service] / [data-service-slide]   the sticky services panel follows the row in view (desktop)
    [data-process] / [data-process-fill] / [data-step]   the method line fills, steps light up
    [data-col-speed="n"]          gallery columns drift at different speeds (desktop)
    [data-count-to]               rating counter
    a[data-scroll]                smooth anchor links
  Reduced motion: no loader, no Lenis, no scroll animation; content is visible via CSS.
*/

type Cleanup = () => void;

/** Tells the inline failsafe in layout.tsx that the intro took over. */
function markIntroStarted() {
  (window as Window & { __introStarted?: boolean }).__introStarted = true;
}

const DAY_NAMES: Record<"fr" | "en", string[]> = {
  fr: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  en: ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
};
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Day indexes (0 = Sunday) named in an hours label: "Lundi", "Lun – Ven", "Mardi au samedi". */
function daysOf(label: string, names: string[]): number[] {
  const found: number[] = [];
  for (const t of fold(label).split(/[^a-z]+/)) {
    if (t.length < 3) continue;
    const i = names.findIndex((n) => n.startsWith(t) || t.startsWith(n));
    if (i !== -1) found.push(i);
  }
  if (found.length < 2) return found;
  const out: number[] = [];
  const last = found[found.length - 1];
  for (let d = found[0], k = 0; k < 7; k++, d = (d + 1) % 7) {
    out.push(d);
    if (d === last) break;
  }
  return out;
}

export default function Motion() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups: Cleanup[] = [];
    let disposed = false;
    let lenis: Lenis | null = null;
    let menuOpen = false;
    let menuTL: gsap.core.Timeline | null = null;

    const on = (
      target: Window | Document | HTMLElement,
      type: string,
      fn: (e: never) => void,
      opts?: AddEventListenerOptions,
    ) => {
      const listener = fn as unknown as EventListener;
      target.addEventListener(type, listener, opts);
      cleanups.push(() => target.removeEventListener(type, listener, opts));
    };

    const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) =>
      root.querySelector<T & Element>(sel) as T | null;
    const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) =>
      Array.from(root.querySelectorAll(sel)) as T[];

    /* ── photo fade-in (always on) ── */
    $$<HTMLImageElement>(".ph img").forEach((img) => {
      const ph = img.closest(".ph");
      if (!ph) return;
      if (img.complete && img.naturalWidth > 0) ph.classList.add("loaded");
      else on(img, "load", () => ph.classList.add("loaded"), { once: true });
      on(img, "error", () => ph.classList.add("loaded"), { once: true });
    });

    /* ── today's hours (always on) ── */
    const lang = document.documentElement.lang === "en" ? "en" : "fr";
    const today = new Date().getDay();
    $$("[data-hours]").forEach((list) => {
      const rows = $$("[data-day]", list).filter((row) => daysOf(row.dataset.day || "", DAY_NAMES[lang]).includes(today));
      if (!rows.length) return;
      rows.forEach((row) => row.classList.add("is-today"));
      list.setAttribute("data-hours-matched", "");
      const cell = list.closest<HTMLElement>("[data-hours-cell]");
      cell?.setAttribute("data-today", "");
      cleanups.push(() => {
        rows.forEach((row) => row.classList.remove("is-today"));
        list.removeAttribute("data-hours-matched");
        cell?.removeAttribute("data-today");
      });
    });

    /* ── before / after sliders (always on) ── */
    const sliders: { frame: HTMLElement; set: (v: number) => void; touched: () => boolean }[] = [];
    $$("[data-reveal-frame]").forEach((frame) => {
      const range = $<HTMLInputElement>("[data-reveal-range]", frame);
      let touched = false;
      let dragging = false;
      const set = (v: number) => {
        const c = Math.max(0, Math.min(100, v));
        frame.style.setProperty("--pos", c.toFixed(2));
        if (range) range.value = String(Math.round(c));
      };
      const at = (e: PointerEvent) => {
        const r = frame.getBoundingClientRect();
        return r.width ? ((e.clientX - r.left) / r.width) * 100 : 50;
      };
      set(range ? Number(range.value) : 55);
      if (range) on(range, "input", () => { touched = true; set(Number(range.value)); });
      on(frame, "pointerdown", (e: PointerEvent) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        if (e.pointerType === "mouse") e.preventDefault();
        dragging = true;
        touched = true;
        frame.classList.add("is-dragging");
        try { frame.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
        set(at(e));
      });
      on(frame, "pointermove", (e: PointerEvent) => { if (dragging) set(at(e)); });
      const stop = () => { if (!dragging) return; dragging = false; frame.classList.remove("is-dragging"); };
      on(frame, "pointerup", stop);
      on(frame, "pointercancel", stop);
      on(frame, "lostpointercapture", stop);
      cleanups.push(() => { frame.style.removeProperty("--pos"); frame.classList.remove("is-dragging"); });
      sliders.push({ frame, set, touched: () => touched });
    });

    /* ── header state ── */
    const header = $("#header");
    const updateHeader = () => header?.classList.toggle("is-scrolled", window.scrollY > 30);
    on(window, "scroll", updateHeader, { passive: true });
    updateHeader();

    /* ── mobile menu ── */
    const burger = $<HTMLButtonElement>("#burger");
    const menu = $("#mobileMenu");

    const openMenu = () => {
      if (!burger || !menu) return;
      menuOpen = true;
      burger.classList.add("is-open");
      burger.setAttribute("aria-expanded", "true");
      burger.setAttribute("aria-label", burger.dataset.labelClose || "");
      menu.removeAttribute("aria-hidden");
      menu.inert = false;
      if (menuTL) menuTL.play();
      else { menu.style.visibility = "visible"; menu.style.opacity = "1"; }
      document.body.style.overflow = "hidden";
      lenis?.stop();
      $<HTMLElement>("nav a", menu)?.focus({ preventScroll: true });
    };
    const closeMenu = () => {
      if (!menuOpen || !burger || !menu) return;
      menuOpen = false;
      burger.classList.remove("is-open");
      burger.setAttribute("aria-expanded", "false");
      burger.setAttribute("aria-label", burger.dataset.labelOpen || "");
      if (menu.contains(document.activeElement)) burger.focus({ preventScroll: true });
      menu.setAttribute("aria-hidden", "true");
      menu.inert = true;
      if (menuTL) menuTL.reverse();
      else { menu.style.visibility = "hidden"; menu.style.opacity = "0"; }
      document.body.style.overflow = "";
      lenis?.start();
    };
    if (burger && menu) {
      menu.inert = true;
      on(burger, "click", () => (menuOpen ? closeMenu() : openMenu()));
      on(document, "keydown", (e: KeyboardEvent) => {
        if (e.key === "Escape" && menuOpen) { closeMenu(); burger.focus(); }
      });
      cleanups.push(() => {
        menu.inert = false;
        menu.style.visibility = "";
        menu.style.opacity = "";
      });
    }

    /* ── anchors ── */
    const headerOffset = () => (header?.offsetHeight || 72) - 1;
    $$<HTMLAnchorElement>("a[data-scroll]").forEach((a) => {
      on(a, "click", (e: MouseEvent) => {
        const hash = a.getAttribute("href");
        if (!hash || hash.charAt(0) !== "#") return;
        const target = document.querySelector<HTMLElement>(hash);
        if (!target) return;
        e.preventDefault();
        closeMenu();
        if (lenis) {
          lenis.scrollTo(target, {
            offset: hash === "#top" ? 0 : -headerOffset(),
            duration: 1.4,
            easing: (t: number) => 1 - Math.pow(1 - t, 4),
          });
        } else {
          const top = hash === "#top" ? 0 : target.getBoundingClientRect().top + window.scrollY - headerOffset();
          window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
        }
      });
    });

    /* ── mobile dock ── */
    const dock = $("#dock");
    const showDock = (v: boolean) => dock?.classList.toggle("is-visible", v);

    const dispose = () => {
      disposed = true;
      for (let i = cleanups.length - 1; i >= 0; i--) cleanups[i]();
      document.body.style.overflow = "";
    };

    /* ─────────────── Reduced motion path ─────────────── */
    if (reduced) {
      markIntroStarted();
      const loader = $("#loader");
      if (loader) loader.style.display = "none";
      showDock(true);
      cleanups.push(() => { if (loader) loader.style.display = ""; showDock(false); });
      return dispose;
    }

    gsap.registerPlugin(ScrollTrigger, CustomEase, SplitText);
    CustomEase.create("precision", "M0,0 C0.16,1 0.3,1 1,1");
    ScrollTrigger.config({ ignoreMobileResize: true });

    const colText = rgbString(resolveCssColor("--color-text", [236, 234, 229]));
    const colMetal = rgbString(resolveCssColor("--color-secondary", [164, 172, 180]));

    const ctx = gsap.context(() => {});
    const mm = gsap.matchMedia();
    cleanups.push(() => { mm.revert(); ctx.revert(); });

    /* ── mobile menu timeline ── */
    if (menu) {
      ctx.add(() => {
        menuTL = gsap.timeline({ paused: true });
        menuTL.set(menu, { visibility: "visible" });
        menuTL.fromTo(menu, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4, ease: "power2.out" });
        menuTL.fromTo($$("nav a", menu), { autoAlpha: 0, y: 26 }, { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.05, ease: "precision" }, "-=0.2");
        const foot = $(".mobile-menu__foot", menu);
        if (foot) menuTL.fromTo(foot, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5, ease: "precision" }, "-=0.35");
      });
    }

    /* ── Lenis ── */
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    const lenisRef = lenis;
    lenisRef.on("scroll", ScrollTrigger.update);
    const tickLenis = (time: number) => lenisRef.raf(time * 1000);
    gsap.ticker.add(tickLenis);
    gsap.ticker.lagSmoothing(0);
    cleanups.push(() => {
      gsap.ticker.remove(tickLenis);
      gsap.ticker.lagSmoothing(500, 33);
      lenisRef.destroy();
      lenis = null;
    });

    // The intro always plays from the top
    const prevRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    cleanups.push(() => { history.scrollRestoration = prevRestoration; });

    // Nobody scrolls while the loader runs
    lenisRef.stop();
    document.body.style.overflow = "hidden";

    /* ── Loader: a paint line that fills ── */
    const loader = $("#loader");
    const counterEl = $("#loaderCount");
    const fillEl = $("#loaderFill");
    const setCount = (v: number) => {
      if (counterEl) counterEl.textContent = String(Math.round(v));
      if (fillEl) fillEl.style.transform = `scaleX(${(v / 100).toFixed(3)})`;
    };
    cleanups.push(() => {
      if (loader) loader.style.display = "";
      setCount(0);
    });

    const loadDone = new Promise<void>((resolve) => {
      if (document.readyState === "complete") resolve();
      else on(window, "load", () => resolve(), { once: true });
    });
    const fontsDone = document.fonts?.ready ? document.fonts.ready.then(() => undefined, () => undefined) : Promise.resolve();
    let patienceId = 0;
    const patience = new Promise<void>((resolve) => { patienceId = window.setTimeout(resolve, 3500); });
    cleanups.push(() => clearTimeout(patienceId));

    const progress = { v: 0 };
    let fakeTween: gsap.core.Tween | null = null;
    ctx.add(() => {
      fakeTween = gsap.to(progress, { v: 86, duration: 1.5, ease: "power2.out", onUpdate: () => setCount(progress.v) });
    });

    Promise.race([Promise.all([loadDone, fontsDone]), patience]).then(() => {
      if (disposed) return;
      ctx.add(() => {
        fakeTween?.kill();
        gsap.to(progress, {
          v: 100, duration: 0.35, ease: "power1.in",
          onUpdate: () => setCount(progress.v),
          onComplete: () => { if (!disposed) ctx.add(begin); },
        });
      });
    });

    /* ─────────────── Entrance ─────────────── */
    function begin() {
      markIntroStarted();
      document.body.style.overflow = "";
      lenisRef.start();

      const tl = gsap.timeline({ defaults: { ease: "precision" } });

      if (loader) {
        tl.to(loader, {
          clipPath: "inset(0 0 100% 0)", duration: 0.9, ease: "power3.inOut",
          onComplete: () => { loader.style.display = "none"; },
        });
      }

      const cover = $(".hero__media img");
      if (cover) tl.fromTo(cover, { opacity: 0, scale: 1.14 }, { opacity: 1, scale: 1, duration: 1.9, ease: "power2.out" }, loader ? "-=0.7" : 0);

      const title = $("#heroTitle");
      if (title) {
        let chars: Element[] = [title];
        try {
          chars = new SplitText(title, { type: "lines,chars", mask: "lines", linesClass: "line", charsClass: "char" }).chars;
        } catch { /* keep whole title */ }
        tl.set(title, { visibility: "visible" }, cover ? "-=1.5" : loader ? "-=0.3" : 0);
        tl.fromTo(chars, { yPercent: 110, color: colMetal }, { yPercent: 0, duration: 1.0, stagger: 0.035 }, "<");
        tl.to(chars, { color: colText, duration: 0.9, stagger: 0.02, ease: "power1.inOut" }, "-=0.55");
      }

      const sheen = $("#heroSheen");
      if (sheen) tl.add(() => sheen.classList.add("is-lit"), "-=1.0");

      const tagline = $("#heroTagline");
      if (tagline) tl.fromTo(tagline, { visibility: "visible", autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.8 }, "-=0.9");

      const heroReveals = $$(".hero [data-reveal]");
      if (heroReveals.length) tl.fromTo(heroReveals, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1 }, "-=0.7");

      const cue = $("#heroCue");
      if (cue) tl.fromTo(cue, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, "-=0.4");

      tl.add(() => { if (!disposed) ctx.add(initScroll); }, 1.1);
    }

    /* ─────────────── Scroll choreography ─────────────── */
    function initScroll() {
      /* Generic reveals (outside hero) */
      $$("[data-reveal]").forEach((el) => {
        if (el.closest(".hero")) return;
        gsap.fromTo(el, { autoAlpha: 0, y: 24 }, {
          autoAlpha: 1, y: 0, duration: 0.9, ease: "precision",
          scrollTrigger: { trigger: el, start: "top 90%", once: true },
        });
      });

      /* Masked line reveals for display headings */
      $$("[data-heading]").forEach((el) => {
        let lines: Element[];
        try {
          lines = new SplitText(el, { type: "lines", mask: "lines" }).lines;
        } catch {
          gsap.set(el, { visibility: "visible" });
          gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1, scrollTrigger: { trigger: el, start: "top 88%", once: true } });
          return;
        }
        gsap.set(el, { visibility: "visible" });
        gsap.fromTo(lines, { yPercent: 110 }, {
          yPercent: 0, duration: 1.0, stagger: 0.09, ease: "precision",
          scrollTrigger: { trigger: el, start: "top 88%", once: true },
        });
      });

      /* Photo parallax inside frames */
      $$("[data-parallax]").forEach((el) => {
        const amount = parseFloat(el.dataset.parallax || "") || 6;
        gsap.fromTo(el, { yPercent: amount }, {
          yPercent: -amount, ease: "none",
          scrollTrigger: { trigger: el.closest(".ph") || el, start: "top bottom", end: "bottom top", scrub: true },
        });
      });

      /* Hero cover drifts as you leave */
      const media = $(".hero__media");
      if (media) {
        gsap.to(media, {
          yPercent: 18, ease: "none",
          scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true },
        });
      }

      /* Progress line */
      const progressEl = $("#progress");
      if (progressEl) {
        ScrollTrigger.create({
          start: 0, end: "max",
          onUpdate: (self) => { progressEl.style.transform = `scaleX(${self.progress.toFixed(4)})`; },
        });
        cleanups.push(() => { progressEl.style.transform = ""; });
      }

      /* Mobile dock appears once the hero is left */
      if (dock) {
        ScrollTrigger.create({
          trigger: ".hero", start: "bottom 85%",
          onEnter: () => showDock(true), onLeaveBack: () => showDock(false),
        });
        cleanups.push(() => showDock(false));
      }

      /* Services: the sticky panel follows the row in view (desktop) */
      const rows = $$("[data-service]");
      const slides = $$("[data-service-slide]");
      const list = $("#servicesList");
      if (rows.length && slides.length) {
        mm.add("(min-width: 900px)", () => {
          const activate = (i: number) => {
            rows.forEach((r) => r.classList.toggle("is-active", Number(r.dataset.service) === i));
            list?.classList.add("has-active");
            let slide = slides.find((s) => Number(s.dataset.serviceSlide) === i);
            if (!slide) {
              const before = slides.filter((s) => Number(s.dataset.serviceSlide) < i);
              slide = before[before.length - 1] || slides[0];
            }
            slides.forEach((s) => s.classList.toggle("is-active", s === slide));
          };
          rows.forEach((row) => {
            const i = Number(row.dataset.service);
            ScrollTrigger.create({
              trigger: row, start: "top 58%", end: "bottom 58%",
              onEnter: () => activate(i), onEnterBack: () => activate(i),
            });
          });
          return () => {
            rows.forEach((r) => r.classList.remove("is-active"));
            list?.classList.remove("has-active");
            slides.forEach((s, k) => s.classList.toggle("is-active", k === 0));
          };
        });
      }

      /* Before / after: an intro sweep from "before" to the resting position */
      sliders.forEach(({ frame, set, touched }) => {
        ScrollTrigger.create({
          trigger: frame, start: "top 72%", once: true,
          onEnter: () => {
            if (touched()) return;
            const p = { v: 100 };
            gsap.to(p, { v: 55, duration: 1.6, ease: "power3.inOut", onUpdate: () => { if (!touched()) set(p.v); } });
          },
        });
      });

      /* The method: the line fills, each step lights up as the fill passes its marker */
      const track = $("[data-process]");
      const fill = track ? $("[data-process-fill]", track) : null;
      if (track && fill) {
        const steps = $$("[data-step]", track);
        let ratios: number[] = [];
        const measure = () => {
          const t = track.getBoundingClientRect();
          const horizontal = window.matchMedia("(min-width: 900px)").matches;
          ratios = steps.map((s) => {
            const r = s.getBoundingClientRect();
            return horizontal ? (r.left - t.left) / Math.max(1, t.width) : (r.top - t.top) / Math.max(1, t.height);
          });
        };
        measure();
        ScrollTrigger.addEventListener("refresh", measure);
        cleanups.push(() => ScrollTrigger.removeEventListener("refresh", measure));
        gsap.fromTo(fill, { scaleX: 0, scaleY: 0 }, {
          scaleX: 1, scaleY: 1, ease: "none",
          scrollTrigger: {
            trigger: track, start: "top 72%", end: "bottom 42%", scrub: 0.5,
            onUpdate: (self) => {
              steps.forEach((s, i) => s.classList.toggle("is-on", self.progress >= (ratios[i] ?? 0) + 0.02));
            },
          },
        });
        cleanups.push(() => steps.forEach((s) => s.classList.remove("is-on")));
      }

      /* Gallery columns drift (desktop) */
      const cols = $$("[data-col-speed]");
      if (cols.length) {
        mm.add("(min-width: 900px)", () => {
          const zone = cols[0].closest("section") || cols[0].parentElement || cols[0];
          cols.forEach((col) => {
            const s = parseFloat(col.dataset.colSpeed || "") || 0;
            if (!s) return;
            gsap.fromTo(col, { yPercent: s }, {
              yPercent: -s, ease: "none",
              scrollTrigger: { trigger: zone, start: "top bottom", end: "bottom top", scrub: true },
            });
          });
        });
      }

      /* Rating counter */
      const reviews = $("#reviews");
      const score = reviews ? $("[data-count-to]", reviews) : null;
      if (reviews && score) {
        const to = parseFloat(score.dataset.countTo || "0");
        const decimals = parseInt(score.dataset.decimals || "0", 10);
        const loc = score.dataset.locale || "fr-FR";
        const fmt = new Intl.NumberFormat(loc, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        const finalText = score.textContent;
        score.textContent = fmt.format(0);
        cleanups.push(() => { score.textContent = finalText; });
        const s = { v: 0 };
        ScrollTrigger.create({
          trigger: reviews, start: "top 70%", once: true,
          onEnter: () => {
            gsap.to(s, { v: to, duration: 1.8, ease: "power2.out", onUpdate: () => { score.textContent = fmt.format(s.v); } });
          },
        });
      }

      ScrollTrigger.sort();
      ScrollTrigger.refresh();

      // Late-arriving fonts change line wraps and section heights
      document.fonts?.ready.then(() => { if (!disposed) ScrollTrigger.refresh(); }, () => undefined);
    }

    return dispose;
  }, []);

  return null;
}
